import Util from '../../util.js';

class Datalist extends HTMLElement {

    #template = document.createElement('template');
    $templateMarkup = `
        <input type="text" list="datalist-options" autocomplete="off" />
        <datalist id="datalist-options">
            <slot></slot>
        </datalist>
    `;

    constructor() {
        super();
        this.attachShadow({mode: 'open'});
        this.addStyle = Util.addStyle;
        this.addStyle('/src/styles/neptune.css');
        this.addStyle('/src/components/form/datalist/datalist.css');
        this.#template.innerHTML = this.$templateMarkup;
        this.shadowRoot.appendChild(this.#template.content.cloneNode(true));
    }

    connectedCallback() {
        this.setDatalist();
        this.setupEventListeners();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    static get observedAttributes() {
        return ['error', 'disabled', 'placeholder', 'value'];
    }

    #validateOptions() {
        const options = Array.from(this.querySelectorAll('option'));
        
        if (options.length === 0) {
            const errorMessage = 'Invalid <neptune-datalist>: Datalist component must contain at least one <option> element.';
            throw new Error(errorMessage);
        }

        const hasValidOption = options.some(option => {
            const value = (option.getAttribute('value') || '').trim();
            return value !== '';
        });

        if (!hasValidOption) {
            const errorMessage = 'Invalid <neptune-datalist>: Datalist component must contain at least one valid <option> with a value attribute.';
            console.error(errorMessage);
            throw new Error(errorMessage);
        }
    }

    setDatalist() {
        this.#validateOptions();
        
        const inputElement = this.shadowRoot.querySelector('input');
        const datalistElement = this.shadowRoot.querySelector('datalist');
        
        datalistElement.innerHTML = '';
        
        this.#populateOptions(datalistElement);

        const placeholder = this.getAttribute('placeholder');
        if (placeholder) {
            inputElement.placeholder = placeholder;
        } else {
            inputElement.removeAttribute('placeholder');
        }

        const value = this.getAttribute('value');
        if (value) {
            inputElement.value = value;
        }

        inputElement.disabled = this.hasAttribute('disabled');
    }

    #populateOptions(datalistElement) {
        const options = Array.from(this.querySelectorAll('option'));
        
        options.forEach(option => {
            const clonedOption = option.cloneNode(true);
            datalistElement.appendChild(clonedOption);
        });
    }

    setupEventListeners() {
        const inputElement = this.shadowRoot.querySelector('input');
        
        this._handleInput = this.handleInput.bind(this);
        this._handleChange = this.handleChange.bind(this);
        
        inputElement.addEventListener('input', this._handleInput);
        inputElement.addEventListener('change', this._handleChange);
    }

    removeEventListeners() {
        const inputElement = this.shadowRoot.querySelector('input');
        if (inputElement && this._handleInput) {
            inputElement.removeEventListener('input', this._handleInput);
            inputElement.removeEventListener('change', this._handleChange);
        }
    }

    handleInput(e) {
        this.dispatchEvent(new CustomEvent('input', {
            detail: { value: e.target.value },
            bubbles: true,
            composed: true
        }));
    }

    handleChange(e) {
        this.setAttribute('value', e.target.value);
        this.dispatchEvent(new CustomEvent('change', {
            detail: { value: e.target.value },
            bubbles: true,
            composed: true
        }));
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'disabled') {
            const inputElement = this.shadowRoot.querySelector('input');
            if (inputElement) {
                inputElement.disabled = this.hasAttribute('disabled');

                if (inputElement.disabled) {
                    inputElement.setAttribute('aria-disabled', 'true');
                } else {
                    inputElement.removeAttribute('aria-disabled');
                }
            }
            return;
        }
        if (name === 'error') {
            const inputElement = this.shadowRoot.querySelector('input');
            if (inputElement) {
                if (this.hasAttribute('error')) {
                    inputElement.setAttribute('aria-invalid', 'true');
                } else {
                    inputElement.removeAttribute('aria-invalid');
                }
            }
            return;
        }
        if (name === 'placeholder') {
            const inputElement = this.shadowRoot.querySelector('input');
            if (inputElement) {
                if (newValue) {
                    inputElement.placeholder = newValue;
                } else {
                    inputElement.removeAttribute('placeholder');
                }
            }
            return;
        }
        if (name === 'value') {
            const inputElement = this.shadowRoot.querySelector('input');
            if (inputElement && oldValue !== newValue) {
                inputElement.value = newValue || '';
            }
            return;
        }
        if (oldValue !== newValue && this.isConnected) {
            this.setDatalist();
        }
    }

    get value() {
        const inputElement = this.shadowRoot.querySelector('input');
        return inputElement ? inputElement.value : '';
    }

    set value(val) {
        const inputElement = this.shadowRoot.querySelector('input');
        if (inputElement) {
            inputElement.value = val;
            this.setAttribute('value', val);
        }
    }
}

customElements.define('neptune-datalist', Datalist);

export default Datalist;